(ns client-portal.client.reports.individual.data
  (:require [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.reports.list.data :as reports.list.d]
            [client-portal.client.router :as cp.router]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.sessions.data :as sessions.d]
            [client-portal.client.routes :as routes]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.style :as style]
            [shared.core.util :as util]
            ;; html2canvas-pro (not stock html2canvas) — the fork supports modern CSS
            ;; color functions like oklch() that the design system uses.
            ["html2canvas-pro" :default html2canvas]
            ;; jspdf is resolved to the `jspdf` global (loaded via a script tag) — its
            ;; bundles don't pass the Closure compiler. See project.clj :js-options :resolve.
            ["jspdf" :refer (jsPDF)]))

(def root-path [:reports :individual])
(def by-id-path (conj root-path :by-id))
;; The report's chrome — which concepts it targets, which filter controls it shows —
;; fetched on its own ahead of the report. Separate from the report so a reload (which
;; clears the report to refetch it) doesn't lose the filters its own request depends on.
(def settings-by-id-path (conj root-path :settings-by-id))
(def generating-pdf-path (conj root-path :generating-pdf?))
;; DOM ids the PDF export uses: the whole content container (for the title header)
;; and the report grid root (whose direct children are the top-level rows/blocks).
(def pdf-capture-element-id "report-pdf-content")
(def pdf-grid-root-id "report-pdf-grid-root")
(def refresh-timer-kind :reports-individual-refresh)
(def refresh-timer-id :current-report)

(defn nav-report-id-for-path [db route-path]
  (some->> (util/when-not-loading (get-in db (reports.list.d/nav-list-path db)))
           (some (fn [r]
                   (when (= route-path (get-in r [:navigation-configuration :route]))
                     (:public-id r))))))

(defn nav-takeover-route? [db]
  (and (not (:report-id (router/current-path-params db)))
       (contains? routes/routes (router/current-route-name db))))

(re/reg-sub
  :reports/nav-takeover-route?-
  (fn [db]
    (nav-takeover-route? db)))

(defn current-report-id [db]
  (or (:report-id (router/current-path-params db))
      (nav-report-id-for-path db (or (cp.router/route->path (router/current-route-name db)
                                                            (router/current-path-params db))
                                     (.. js/window -location -pathname)))))

(def report-filter-options
  "The filter controls a report's configuration can turn off, with the defaults the
   layout applies when it says nothing (mirroring
   shared.report-render/default-enabled-filters) and the request keys each one carries.
   Concept is absent on purpose: a report always targets one, so its control is forced."
  [{:key :location :config-key "location" :label "Location" :default true
    :param-keys [:location-id]}
   {:key :date :config-key "date" :label "Date Range" :default true
    :param-keys [:start-date :end-date :end-date-start-ms]}
   {:key :crew-type :config-key "crewType" :label "Crew Type" :default false
    :param-keys [:crew-type]}])

(defn report-filters
  "The filter visibility a report declares, defaulted for anything it leaves out.
   Takes the `filters` map itself (string or kebab keys), not the whole configuration."
  [declared-filters]
  (let [declared (util/->kebab-case-keywords declared-filters)]
    (->> report-filter-options
         (map (fn [{:keys [key default]}]
                [key (boolean (get declared key default))]))
         (into {}))))

(defn without-hidden-filter-params
  "Drop the request values for the filter categories a report hides — a control the
   report does not show has no value to contribute. `filters` nil means the report has
   not been loaded yet and nothing is known, in which case everything is sent and the
   server's own gating (shared.report-render/enabled-filters) drops the same keys."
  [params filters]
  (if (nil? filters)
    params
    (reduce (fn [acc {:keys [key param-keys]}]
              (if (get filters key)
                acc
                (apply dissoc acc param-keys)))
            params
            report-filter-options)))

(re/reg-auth0-loader
  :reports/load-settings
  (fn [[[_ report-id]]]
    (conj settings-by-id-path report-id))
  (fn [db [_ report-id]]
    (when-let [concept-id (concepts.d/current-concept-id db)]
      (when report-id
        {:method :get
         :url (str "/api/reports/i/" report-id "/settings")
         :query-params {:concept-id concept-id}})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :error-transf (fn [] {})
  :on-success (fn [db {:keys [body]}]
                (when-not (nav-takeover-route? db)
                  (->> (util/->kebab-case-keywords body)
                       (:target-concept-ids)
                       (concepts.d/select-first-target-concept! db)))
                db))

(re/reg-auth0-load-sub
  :reports/settings-
  (fn [_ report-id]
    (conj settings-by-id-path report-id))
  :reports/load-settings)

(defn settings [db report-id]
  (util/when-not-loading (get-in db (conj settings-by-id-path report-id))))

(defn settings-filters
  "The report's filter visibility, or nil while its settings are still in flight — which
   callers read as \"not known yet\" and hold their filter controls back for."
  [db report-id]
  (when-let [loaded (settings db report-id)]
    (report-filters (:filters loaded))))

(re/reg-auth0-loader
  :reports/load-individual-data
  (fn [[[_ report-id]]]
    (conj by-id-path report-id))
  (fn [db [_ report-id]]
    (when report-id
      {:method :post
       :url (str "/api/reports/i/" report-id)
       :json-params (-> (vi.d/base-query-args db {:use-day-part? true})
                        (without-hidden-filter-params (settings-filters db report-id)))}))
  :transf #(let [b (some-> % :body (util/->kebab-case-keywords))]
             (if (empty? b)
               b
               (update b :configuration util/json-string->map-basic)))
  :error-transf (fn [] {}))

(re/reg-auth0-load-sub
  :reports/individual-data-
  (fn [_ report-id]
    (conj by-id-path report-id))
  :reports/load-individual-data)

(re/reg-sub :reports/current-report-id- current-report-id)

(re/reg-event-db
  :reports/reload-individual-data re/middleware
  (fn [db]
    (let [report-id (current-report-id db)]
      (if (and report-id (not (util/loading? (get-in db (conj by-id-path report-id)))))
        (assoc-in db (conj by-id-path report-id) nil)
        db))))

(re/reg-event-db
  :reports/reload-individual-data-background re/middleware
  (fn [db]
    (when-let [report-id (current-report-id db)]
      (re/dispatch [:reports/load-individual-data [nil report-id] :reload]))
    db))

(re/reg-event-db
  :reports/set-refresh-timer re/middleware
  (fn [db [refresh-seconds]]
    (when (some-> refresh-seconds (pos?))
      (re/dispatch [:timer/set-timer
                    refresh-timer-kind
                    refresh-timer-id
                    (* refresh-seconds 1000)
                    #(re/dispatch-once [:reports/reload-individual-data-background])
                    {:fire-first? false
                     :repeat? true
                     :extra-on-fail #(js/console.error (str "Report refresh timer failed: " %))}]))
    db))

(re/reg-event-db
  :reports/clear-refresh-timer re/middleware
  (fn [db]
    (re/dispatch [:timer/clear-timer refresh-timer-kind refresh-timer-id])
    db))

(re/reg-event-fx
  :reports/initialize-individual re/middleware
  (fn [_]
    (re/dispatch [:voice-insights/initialize-filters])
    {:forward-events {:register :reports-individual-initialization-listener
                      :events #{:concepts/select :locations/select :core/select-crew-type
                                :core/select-timeframe :locations/clear-selected
                                :core/timeframe-date-range-changed :voice-insights/select-day-part}
                      :dispatch-to [:reports/reload-individual-data]}}))

(re/reg-event-db
  :reports/seed-default-filter-params re/middleware
  (fn [db [report-id]]
    (let [dfv (get-in db (conj by-id-path report-id :configuration "defaultFilterValues"))
          current-qps (router/current-query-params db)
          ;; Seed a report's `defaultFilterValues` into the route, but only for
          ;; params the URL doesn't already carry — an explicit URL value always
          ;; wins over the report default. Once merged, `initialize-filters` reads
          ;; them back into the live filter state and the report reloads with them.
          missing-qps (into {}
                            (remove (fn [[k _]] (contains? current-qps k)))
                            (sessions.d/default-filter-values->session-qps dfv))]
      (when (not-empty missing-qps)
        (re/dispatch [:router/go-with-merged-query-params missing-qps
                      {:on-route-change (fn []
                                          (re/dispatch [:core/initialize-filters])
                                          (re/dispatch [:reports/reload-individual-data]))}]))
      db)))

;; Navigating this way always clears the cached report data so it reloads fresh.
(re/reg-event-db
  :reports/open-report re/middleware
  (fn [db [report-id query-params]]
    (re/dispatch [:router/go (cond-> [:reports/individual {:report-id report-id}]
                                     (not-empty query-params) (conj query-params))])
    (assoc-in db (conj by-id-path report-id) nil)))

(re/reg-event-db
  :reports/open-list re/middleware
  (fn [db]
    (re/dispatch [:router/go [:reports/list]])
    db))

(def edit-session-path (conj root-path :edit-session))

;; Edit flow: pre-create a builder session seeded from the current report, then hand
;; off to the ai-agent feature to open the builder on the report's edit route.
(re/reg-auth0-loader
  :reports/edit-report
  edit-session-path
  ;; Dispatch args arrive spread here (db, report-id, access-token) but as a
  ;; vector in :on-success.
  (fn [db report-id]
    (let [report-id (or (when (string? report-id) (not-empty report-id))
                        (current-report-id db))
          concept-id (concepts.d/current-concept-id db)]
      (when (and (not-empty report-id) concept-id)
        {:method :post
         :url (str "/api/reports/i/" report-id "/edit-session")
         :json-params {:concept-id concept-id}})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :on-success (fn [db {:keys [body]} [report-id]]
                (let [{:keys [session-id]} (util/->kebab-case-keywords body)]
                  (re/dispatch [:ai-agent/open-report-edit {:report-id (or (not-empty report-id)
                                                                           (current-report-id db))
                                                            :session-id session-id}])
                  db))
  :on-fail (fn [db]
             (re/dispatch [:toasts/add-error {:msg "Failed to start report edit" :duration 3000}])
             db))

(re/reg-sub
  :reports/creating-edit-session?-
  (fn [db]
    (util/loading? (get-in db edit-session-path))))

;; Filter values the report actually applies (mirrors the layout-filter visibility
;; defaults in views: location/date on, crew-type off) — used for both the PDF file
;; name and the details line injected under the captured header.
(defn- pdf-filter-parts [db]
  (let [report (util/when-not-loading (get-in db (conj by-id-path (current-report-id db))))
        filters (util/->kebab-case-keywords (get-in report [:configuration "filters"]))
        {:keys [start-date end-date location-id concept-id]} (vi.d/base-query-args db {:use-day-part? true})
        concept-label (:label (concepts.d/current-concept db))
        location-label (when (and location-id (get filters :location true))
                         (some->> (util/when-not-loading (get-in db (conj locations.d/by-concept-id-path concept-id)))
                                  (filter #(= (:id %) location-id))
                                  (first)
                                  :label))
        location-label (if (and (not-empty concept-label)
                                (not-empty location-label)
                                (not= location-label concept-label))
                         (s/trim (s/replace location-label concept-label ""))
                         location-label)
        crew-type (when (get filters :crew-type false)
                    (core.d/selected-crew-type db))
        day-part (let [dp (vi.d/selected-day-part db)]
                   (when (not= dp (:label vi.d/day-part-all-day)) dp))]
    (cond-> []
            (not-empty concept-label) (conj (anon.d/concept-label (anon.d/ctx db) (concepts.d/current-concept db)))
            (not-empty location-label) (conj (anon.d/location-label (anon.d/ctx db) location-label))
            (not-empty crew-type) (conj (util/capitalize-words crew-type))
            (not-empty day-part) (conj (util/capitalize-words day-part))
            (and (get filters :date true) (not-empty start-date) (not-empty end-date))
            (conj (str start-date " to " end-date)))))

(defn- ->pdf-file-name [title filter-parts]
  (str (->> (into [(or (not-empty title) "report")] filter-parts)
            (map util/->kebab-case-string)
            (s/join "-"))
       ".pdf"))

;; The report body wrapper is transparent — walk up to the first ancestor with a
;; real background so the PDF matches the page (and respects light/dark theme).
(defn- effective-background-color [el]
  (loop [node el]
    (if (nil? node)
      "rgb(255, 255, 255)"
      (let [bg (.-backgroundColor (js/getComputedStyle node))]
        (if (and (not-empty bg)
                 (not= bg "transparent")
                 (not= bg "rgba(0, 0, 0, 0)"))
          bg
          (recur (.-parentElement node)))))))

(defn- rgb-parts [color]
  (some->> color
           (re-find #"(\d+)[,\s]+(\d+)[,\s]+(\d+)")
           (rest)
           (mapv js/parseInt)))

;; Client-side PDF export. Rather than one tall screenshot sliced at fixed page
;; heights (which cuts through components), each block — the title header plus each
;; top-level report row — is rasterized separately and placed on a page, starting a
;; new page whenever the next block won't fit. A block taller than a full page is
;; still sliced across pages as a fallback.
(defn- generate-report-pdf! [{:keys [element-id grid-root-id file-name header-details on-done]}]
  (let [container (js/document.getElementById element-id)
        grid-root (some-> grid-root-id (js/document.getElementById))]
    (if (and container grid-root)
      (let [bg (effective-background-color container)
            bg-rgb (rgb-parts bg)
            header (.-firstElementChild container)
            blocks (into (if header [header] [])
                         (array-seq (js/Array.from (.-children grid-root))))
            base-opts {:scale 2
                       :useCORS true
                       :backgroundColor bg}
            opts (clj->js base-opts)
            ;; The filter details only exist in the PDF: html2canvas captures a CLONE
            ;; of the header, and `onclone` appends them to that clone — the page's
            ;; own header never shows them.
            header-opts (if (not-empty header-details)
                          (clj->js (assoc base-opts
                                          :onclone (fn [cloned-doc cloned-el]
                                                     (let [el (.createElement cloned-doc "div")]
                                                       (set! (.-textContent el) header-details)
                                                       (set! (.. el -style -cssText)
                                                             (str "color:" (style/alc-color "Text/secondary") ";"
                                                                  "font-size:24px;"
                                                                  "line-height:1.33;"))
                                                       (.appendChild cloned-el el)))))
                          opts)]
        (-> (js/Promise.all (into-array (mapv #(html2canvas % (if (= % header) header-opts opts)) blocks)))
            (.then
              (fn [canvases]
                (let [pdf (jsPDF. (clj->js {:orientation "portrait"
                                            :unit "px"
                                            :format "a4"
                                            :compress true}))
                      margin 24
                      gap 12
                      page-w (.. pdf -internal -pageSize (getWidth))
                      page-h (.. pdf -internal -pageSize (getHeight))
                      content-w (- page-w (* 2 margin))
                      usable-h (- page-h (* 2 margin))
                      max-y (- page-h margin)
                      paint-bg! (fn []
                                  (when bg-rgb
                                    (.setFillColor pdf (nth bg-rgb 0) (nth bg-rgb 1) (nth bg-rgb 2))
                                    (.rect pdf 0 0 page-w page-h "F")))]
                  (paint-bg!)
                  (loop [cs (array-seq canvases)
                         y margin]
                    (when (not-empty cs)
                      (let [canvas (first cs)
                            img (.toDataURL canvas "image/png")
                            img-h (/ (* (.-height canvas) content-w) (.-width canvas))]
                        (cond
                          ;; Taller than a whole page: give it a fresh page, then slice it.
                          (> img-h usable-h)
                          (do
                            (when (> y margin)
                              (.addPage pdf)
                              (paint-bg!))
                            (loop [pos margin
                                   left img-h]
                              (.addImage pdf img "PNG" margin pos content-w img-h)
                              (let [left' (- left usable-h)]
                                (when (pos? left')
                                  (.addPage pdf)
                                  (paint-bg!)
                                  (recur (- pos usable-h) left'))))
                            (recur (rest cs) (+ margin img-h)))

                          ;; Won't fit in the space left on this page: start a new one.
                          (> (+ y img-h) max-y)
                          (do
                            (.addPage pdf)
                            (paint-bg!)
                            (.addImage pdf img "PNG" margin margin content-w img-h)
                            (recur (rest cs) (+ margin img-h gap)))

                          :else
                          (do
                            (.addImage pdf img "PNG" margin y content-w img-h)
                            (recur (rest cs) (+ y img-h gap)))))))
                  (.save pdf file-name))))
            (.catch (fn [e] (js/console.error "Report PDF export failed:" e)))
            (.finally on-done)))
      (on-done))))

(re/reg-event-db
  :reports/set-generating-pdf re/middleware
  (fn [db [generating?]]
    (assoc-in db generating-pdf-path (boolean generating?))))

(re/reg-event-db
  :reports/generate-pdf re/middleware
  (fn [db [{:keys [title]}]]
    (let [filter-parts (pdf-filter-parts db)]
      (generate-report-pdf! {:element-id pdf-capture-element-id
                             :grid-root-id pdf-grid-root-id
                             :file-name (->pdf-file-name title filter-parts)
                             :header-details (s/join "  ·  " filter-parts)
                             :on-done #(re/dispatch [:reports/set-generating-pdf false])}))
    db))

(re/reg-event-db
  :reports/download-pdf re/middleware
  (fn [db [args]]
    (re/dispatch [:reports/generate-pdf args])
    (assoc-in db generating-pdf-path true)))

(re/reg-sub
  :reports/generating-pdf?-
  (fn [db]
    (get-in db generating-pdf-path)))

(re/reg-event-db
  :reports/refresh-nav-report re/middleware
  (fn [db [route-k]]
    (let [report-id (nav-report-id-for-path db (cp.router/route->path route-k))]
      (if (and report-id (not= report-id (current-report-id db)))
        (assoc-in db (conj by-id-path report-id) nil)
        db))))